import React, { useEffect, useState } from "react";
import { Button, Card, Stack } from "react-bootstrap";
import DatePicker from "./DatePicker";
import SelectFamily from "./SelectFamily";
import {
  fetchUserCaseGuardians,
  fetchUserCaseMembers,
  fetchUserCaseMeetings,
  postMeeting,
  patchMeeting,
  deleteMeeting,
} from "../../services/httpClient";
import IconEmbeddedButton from "./IconEmbeddedButton";
import { faAdd, faArrowRight, faCheck, faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import IconButton from "./IconButton";

const NextMeetingCard = ({ id, setShowToast, setToastText, setToastBody, setErrMsg }: any) => {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [currentMeeting, setCurrentMeeting] = useState<any>(0);
  const [nextMeeting, setNextMeeting] = useState<any>({});
  const [family, setFamily] = useState<any[]>([]);
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [participant, setParticipant] = useState<string>("");
  const [isConfirmingCancel, setIsConfirmingCancel] = useState<boolean>(false);
  const [isRescheduling, setIsRescheduling] = useState<boolean>(false);
  const [isCreatingNext, setIsCreatingNext] = useState<boolean>(false);
  const hasMeeting = meetings.length > 0;

  useEffect(() => {
    getMeetings();
    getFamily();
  }, []);

  useEffect(() => {
    if (!hasMeeting) return;
    setMeetingDate(nextMeeting?.next_meeting_at ?? "");
    if (nextMeeting?.participant_id && nextMeeting?.participant_type) {
      setParticipant(`${nextMeeting.participant_id}|${nextMeeting.participant_type}`);
    } else {
      setParticipant("");
    }
  }, [nextMeeting]);

  const resetUIState = () => {
    setIsConfirmingCancel(false);
    setIsRescheduling(false);
    setIsCreatingNext(false);
  };

  const getMeetings = () => {
    fetchUserCaseMeetings(id).then((res: any) => {
      setMeetings(res[0].data);
      setCurrentMeeting(0);
    });
  };

  const getFamily = () => {
    fetchUserCaseGuardians(id, true).then((res: any) => {
      const guardians = res[0].data
        .filter((f: any) => !f.deleted_at)
        .map((g: any) => ({
          id: g.id,
          type: "guardian",
          name: `${g.firstname} ${g.lastname}`,
        }));

      fetchUserCaseMembers(id, true).then((res: any) => {
        const members = res[0].data
          .filter((f: any) => !f.deleted_at)
          .map((m: any) => ({
            id: m.id,
            type: "member",
            name: `${m.firstname} ${m.lastname}`,
          }));

        setFamily([...guardians, ...members]);
      });
    });
  };

  const saveMeeting = () => {
    const [participant_id, participant_type] = participant.split("|");

    const meeting = {
      next_meeting_at: meetingDate,
      participant_id: participant ? Number(participant_id) : null,
      participant_type: participant ? participant_type : null,
    };

    const request = isCreatingNext ? postMeeting(meeting, id) : patchMeeting(meeting, id, nextMeeting.id);

    request
      .then(() => {
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody("Meeting saved");
        resetUIState();
        setCurrentMeeting(0);
        getMeetings();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const cancelMeeting = () => {
    deleteMeeting(id, nextMeeting.id)
      .then(() => {
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody("Meeting canceled");
        resetUIState();
        setCurrentMeeting(0);
        getMeetings();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const clearDraft = () => {
    if (hasMeeting) {
      setMeetingDate(nextMeeting?.next_meeting_at ?? "");
      if (nextMeeting.participant_id && nextMeeting.participant_type) {
        setParticipant(`${nextMeeting.participant_id}|${nextMeeting.participant_type}`);
      } else {
        setParticipant("");
      }
    } else {
      setMeetingDate("");
      setParticipant("");
    }
    resetUIState();
  };

  const onClickCreateNewMeeting = () => {
    setIsCreatingNext(true);
    setIsRescheduling(true);
    setMeetingDate("");
    setParticipant("");
  };

  const upcomingMeetings = meetings
    .filter((m) => new Date(m.next_meeting_at) > new Date())
    .sort((a, b) => new Date(a.next_meeting_at).getTime() - new Date(b.next_meeting_at).getTime());

  useEffect(() => {
    if (upcomingMeetings.length === 0) {
      setNextMeeting({});
    }

    if (currentMeeting >= upcomingMeetings.length) {
      setCurrentMeeting(0);
      setNextMeeting(upcomingMeetings[0]);
    }

    setNextMeeting(upcomingMeetings[currentMeeting]);
  }, [upcomingMeetings, currentMeeting]);

  const onClickNextMeeting = () => {
    if (upcomingMeetings.length <= 1) return;
    setCurrentMeeting((prev: any) => (prev + 1 < upcomingMeetings.length ? prev + 1 : 0));
  };

  const showMeeting = hasMeeting || isCreatingNext;
  const isReadOnly = hasMeeting && !isRescheduling && !isConfirmingCancel;

  return (
    <>
      <Card className="bg-light mb-5">
        <Card.Body>
          <Stack direction="horizontal" gap={5} className="justify-content-between">
            <h5 className="app-header-text mb-0">Upcoming Meeting</h5>
            <IconEmbeddedButton onClickButton={onClickCreateNewMeeting} tooltipText="New Meeting" icon={faAdd} />
          </Stack>
          {showMeeting && (
            <>
              <Stack gap={3} className="mt-3">
                <DatePicker
                  label="Date and Time"
                  currentValue={meetingDate}
                  setCurrentValue={setMeetingDate}
                  hasMinDate={true}
                  hasTime={true}
                  disabled={isReadOnly || isConfirmingCancel}
                />
                <SelectFamily
                  defaultSelection="Unassigned"
                  skipDefaultSelection={participant ? true : false}
                  family={family}
                  familyUser={participant}
                  updateFamilyUser={(e: any) => setParticipant(e.target.value)}
                  readOnly={isReadOnly || isConfirmingCancel}
                  fullSize={true}
                />
              </Stack>
              {isConfirmingCancel && (
                <div className="mt-4 text-center">
                  <p>Are you sure you want to cancel this meeting?</p>
                  <Button className="app-secondary-color button me-3" onClick={cancelMeeting}>
                    Yes
                  </Button>
                  <Button className="app-danger-color button" onClick={() => setIsConfirmingCancel(false)}>
                    No
                  </Button>
                </div>
              )}
              {!isConfirmingCancel && (
                <div className="mt-4">
                  {isReadOnly && (
                    <Stack direction="horizontal" gap={3} className="justify-content-center">
                      <IconButton onClickButton={() => setIsConfirmingCancel(true)} tooltipText="Cancel" icon={faTrash} />
                      <div className="vr" />
                      <IconButton onClickButton={() => setIsRescheduling(true)} tooltipText="Reschedule" icon={faEdit} />
                      {upcomingMeetings.length > 1 && (
                        <>
                          <div className="vr" />
                          <IconButton onClickButton={onClickNextMeeting} tooltipText="Next" icon={faArrowRight} />
                        </>
                      )}
                    </Stack>
                  )}
                  {(isRescheduling || isCreatingNext) && (
                    <Stack direction="horizontal" gap={3} className="justify-content-center">
                      <IconButton onClickButton={saveMeeting} tooltipText="Save" icon={faCheck} disabled={!meetingDate} />
                      <div className="vr" />
                      <IconButton onClickButton={clearDraft} tooltipText="Cancel" icon={faClose} />
                    </Stack>
                  )}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default NextMeetingCard;
